
import { useRef, useState, FormEvent, ChangeEvent, DragEvent, KeyboardEvent, useEffect } from 'react';
import '../../style/selects.css';
import Translatable from '../translatable_text/Translatable';
import { MAX_COVER_LETTER_LEN, MAX_FILE_SIZE, MAX_NOTES_LEN, MAX_TEXT_LEN, MIN_COVER_LETTER_LEN, validateField } from '@/hooks/validate-field';
import api from '@/context/apiRequest';
import { useMutation, useQuery } from '@tanstack/react-query';
import MsgComponent from '../msg_component/MsgComponent';
import { useSelector } from '../../store/Store';
import { AxiosError } from 'axios';




interface SubProbs {
  id: number;
  name: string;
  description: number;
}

interface SelectionData {
  application_types: {
    id: number;
    name: string;
    created_at: number;
  }[];
  academic_qualifications: SubProbs[]
  departments: SubProbs[];
  nationalities: {
    id: number;
    name: string;
  }[]
}

const gpa_system_data = [
  {
    id: 12545255,
    value: 4,
  },
  {
    id: 13453646,
    value: 5,
  },
]

function JoinForm() {
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const [resumeName, setResumeName] = useState('Drop your resume here or click to upload');
  const [resumeError, setResumeError] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [coverLetterLength, setCoverLetterLength] = useState(0);
  const [notesLength, setNotesLength] = useState(0);
  const [ err, setErr ] = useState('');

  const rtl = useSelector(( state ) => state.customizer.activeDir === 'rtl');

  const sanitize = (value: any): any => {
    if (typeof value !== 'string') return value;
    return value.replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[<>]/g, '')
      .trim();
  };

  const { data } = useQuery({
    queryKey: ["select_data"],
    queryFn: async () =>
      await api().get(`/api/v1/guest/lists`).then((res) => {
        return res.data.data as SelectionData
      })
  })
  
  const mutation = useMutation({
    mutationFn: async (formData: object) => {
      await api().post(`/api/v1/guest/applications`, formData)
    },
    onSuccess: () =>  {
      // Clear all form fields on successful submission
      const form = document.getElementById('application-form') as HTMLFormElement;
      if (form) {
        form.reset();
      }
      
      // Reset all state variables
      setResumeName('Drop your resume here or click to upload');
      setResumeError(false);
      setHoneypot('');
      setFieldErrors({});
      setCoverLetterLength(0);
      setNotesLength(0);
    },
    onError: (error) => {
      console.log(error)
    }
  })

  // Real-time validation handler
  const handleFieldChange = (fieldName: string, value: string) => {
    const validation = validateField(fieldName, value);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: validation.isValid ? '' : (validation.message || '')
    }));
    
    // Update character counters
    if (fieldName === 'cover_letter') {
      setCoverLetterLength(value.length);
    } else if (fieldName === 'notes') {
      setNotesLength(value.length);
    }
  };

  const isAllowedFile = (file: File | null): boolean => {
    if (!file) return false;
    const allowed = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return allowed.includes(file.type);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>, setter: (name: string) => void) => {
    const file = e.target.files && e.target.files[0];
    if (file && isAllowedFile(file) && file.size <= MAX_FILE_SIZE) {
      setter(file.name);
      if (e.target === resumeInputRef.current) setResumeError(false);
    } else if (file) {
      alert('Unsupported file or too large. Only PDF/DOC/DOCX up to 5MB.');
      e.target.value = '';
      setter('');
    }
  };

  const makeDropHandlers = (inputRef: React.RefObject<HTMLInputElement | null>, setter: (name: string) => void) => ({
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.add('is-dragover');
    },
    onDragLeave: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('is-dragover');
    },
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('is-dragover');
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        const file = files[0];
        if (!isAllowedFile(file)) {
          alert('Unsupported file type. Please upload a PDF or DOC/DOCX.');
          return;
        }
        if (inputRef.current) {
          const dt = new DataTransfer();
          dt.items.add(file);
          inputRef.current.files = dt.files;
        }
        setter(file.name);
        if (inputRef.current === resumeInputRef.current) setResumeError(false);
      }
    },
    onClick: () => inputRef.current && inputRef.current.click(),
  });
  
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    try {
            
      // Honeypot: if filled, treat as bot and abort silently
      if (honeypot) {
        return;
      }

      // Enhanced validation for all fields
      const formData = new FormData(form);
      let hasErrors = false;
      const errors: string[] = [];

      // Validate text fields
      const textFields = ['full_name', 'email', 'phone', 'university', 'major', 'gpa', 'gpa_system', 'cover_letter', 'notes'];
      textFields.forEach((fieldName) => {
        const value = formData.get(fieldName) as string || '';
        const validation = validateField(fieldName, value);
        if (!validation.isValid) {
          hasErrors = true;
          errors.push(validation.message || `${fieldName} is invalid`);
        }
      });

      // Validate required resume
      if (resumeInputRef.current && (!resumeInputRef.current.files || resumeInputRef.current.files.length === 0)) {
        setResumeError(true);
        hasErrors = true;
        errors.push('Resume is required');
      } else {
        setResumeError(false);
      }

      // Validate resume file if present
      if (resumeInputRef.current && resumeInputRef.current.files && resumeInputRef.current.files[0]) {
        const f = resumeInputRef.current.files[0];
        if (!isAllowedFile(f) || f.size > MAX_FILE_SIZE) {
          setResumeError(true);
          hasErrors = true;
          errors.push('Resume must be PDF/DOC/DOCX and under 5MB');
        }
      }

      // Check native form validity
      if (!form.checkValidity()) {
        hasErrors = true;
      }

      if (hasErrors) {
        form.reportValidity();
        return;
      }
      
      // Integration point: collect FormData and submit to API endpoint
      const data = new FormData(form);

      // Sanitize and normalize user inputs before sending
      textFields.forEach((key) => {
        const val = data.get(key);
        if (typeof val === 'string') {
          const maxLen = key === 'notes' || key === 'cover_letter' ? MAX_NOTES_LEN : MAX_TEXT_LEN;
          const sanitized = sanitize(val).slice(0, maxLen);
          data.set(key, sanitized);
        }
      });

      const email = data.get('email');
      if (typeof email === 'string') data.set('email', sanitize(email.toLowerCase()).slice(0, 100));
      const phone = data.get('phone');
      if (typeof phone === 'string') data.set('phone', phone.replace(/[^+0-9\-()\s]/g, '').slice(0, 30));


        
      const res = mutation.mutateAsync(data)
      console.log(res)


    } catch (error) {
      if (error instanceof AxiosError) {
        setErr(error.response?.data?.message || error.response?.data?.error || 'Submission failed');
        console.log('Submit error:', error);
      } else {
        setErr('An unexpected error occurred during sumbit');
      }
    }

  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setErr('')
    }, 4000);
  
    // Cleanup function: Clear the timeout when the component unmounts or err changes
    return () => {
      clearTimeout(timer);
    };
    
  }, [err])


  return (
    <section className="contact section-padding sub-bg padded-container">
     
      <div className="max-w-5xl mx-auto px-4">
          <div className="col-span-6">
            <div className="full-width">
              <div className="sec-head mb-50">
                  <h3 className="ltr:hidden text-u ls1"> نموذج {" "} <span className="fw-200"> التقديم </span> للشركة </h3>
                  <h3 className="rtl:hidden text-u ls1">Company{" "}<span className="fw-200">Application</span>{" "}Form</h3>
                <span style={{ color: 'gray' }} className="mt-10"><Translatable>Please complete all required fields. Files must be PDF or DOC/DOCX.</Translatable></span>
              </div>
              <form id="application-form" onSubmit={handleSubmit} noValidate>
                <div className="controls grid grid-cols-12 gap-6">
                  {/* Honeypot field - hidden from users */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="company">Company</label>
                    <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                  </div>
                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="full_name" className="mb-10">
                          <Translatable>
                            Full name
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="full_name"
                        type="text"
                        name="full_name"
                        placeholder={rtl ? "ادخل اسمك الرباعي" : "Enter your full name"}
                        required
                        onChange={(e) => handleFieldChange('full_name', e.target.value)}
                        className={fieldErrors.full_name ? 'error' : ''}
                      />
                      {fieldErrors.full_name && (
                        <span className="error-message">{fieldErrors.full_name}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="email" className="mb-10">
                          <Translatable>
                            Email
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder={rtl ? "ادخل ايميلك الشخصي" : "name@example.com"}
                        required
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        className={fieldErrors.email ? 'error' : ''}
                      />
                      {fieldErrors.email && (
                        <span className="error-message">{fieldErrors.email}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="phone" className="mb-10">
                          <Translatable>
                            Phone
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder={rtl ? "ادخل رقم الهاتف" : "e.g. +966 5X XXX XXXX"}
                        pattern="^\\+?[0-9\\s\\-()]{7,20}$"
                        required
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                        className={fieldErrors.phone ? 'error' : ''}
                      />
                      {fieldErrors.phone && (
                        <span className="error-message">{fieldErrors.phone}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="university" className="mb-10">
                          <Translatable>
                            University
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="university"
                        type="text"
                        name="university"
                        placeholder={rtl ? "ادخل اسم جامعتك" : "Your university"}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="major" className="mb-10">
                          <Translatable>
                            Major
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="major"
                        type="text"
                        name="major"
                        placeholder={rtl ? "ادخل تخصصك الجامعي" : "Your major"}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className='flex gap-4'>
                      <div className="flex-1 form-group mb-30">
                        <div className='flex gap-1'>
                          <label htmlFor="gpa" className="mb-10">
                            <Translatable>
                              GPA
                            </Translatable>
                          </label>
                          <div><span className='required-dot'>*</span></div>
                        </div>
                        <input
                          id="gpa"
                          type="number"
                          name="gpa"
                          inputMode="decimal"
                          min="0"
                          max="5"
                          step="0.01"
                          pattern="^\\d{1}(?:\\.\\d{1,2})?$|^\\d{1}\\.\\d{1,2}$"
                          placeholder={"e.g. 3.75"}
                          required
                        />
                      </div>
                      <div className="form-group mb-30">
                        <div className='flex gap-1'>
                          <label htmlFor="gpa_system" className="mb-10">
                            <Translatable>
                              Max GPA
                            </Translatable>
                          </label>
                          <div><span className='required-dot'>*</span></div>
                        </div>
                        <select id="gpa_system" name="gpa_system" required defaultValue="">
                          <option  value="" disabled><Translatable>Max GPA</Translatable></option>
                          {gpa_system_data?.map((el) => 
                            <option key={el.id} value={el.value}>{el.value}</option>
                          )}
                        </select>
                      </div>

                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="application_type_id" className="mb-10">
                          <Translatable>
                            Application type
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <select id="application_type_id" name="application_type_id" required defaultValue="">
                        <option value="" disabled><Translatable>Select application type</Translatable></option>
                        {data?.application_types?.map((el) => 
                          <option key={el.id} value={el.id}>{el.name}</option>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                    <div className='flex gap-1'>
                        <label htmlFor="academic_qualification_id" className="mb-10">
                          <Translatable>
                            Academic qualification
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <select id="academic_qualification_id" name="academic_qualification_id" required defaultValue="">
                        <option value="" disabled><Translatable>Select qualification</Translatable></option>
                        {data?.academic_qualifications?.map((el) => 
                          <option key={el.id} value={el.id}>{el.name}</option>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="department_id" className="mb-10">
                          <Translatable>
                            Department
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <select id="department_id" name="department_id" required defaultValue="">
                        <option  value="" disabled><Translatable>Select department</Translatable></option>
                        {data?.departments?.map((el) => 
                          <option key={el.id} value={el.id}>{el.name}</option>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="nationality_id" className="mb-10">
                          <Translatable>
                            Nationality
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <select id="nationality_id" name="nationality_id" required defaultValue="">
                        <option value="" disabled><Translatable>Select nationality</Translatable></option>
                        {data?.nationalities?.map((el) => 
                          <option key={el.id} value={el.id}>{el.name}</option>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="col-span-12">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="cover_letter" className="mb-10">
                          <Translatable>
                            Cover letter
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <textarea
                        id="cover_letter"
                        name="cover_letter"
                        placeholder={rtl ? "اخبرنا القليل عن نفسك ..." : "Tell us a little about yourself..."}
                        rows={4}
                        minLength={MIN_COVER_LETTER_LEN}
                        maxLength={MAX_COVER_LETTER_LEN}
                        required
                        onChange={(e) => handleFieldChange('cover_letter', e.target.value)}
                        className={fieldErrors.cover_letter ? 'error' : ''}
                      ></textarea>
                      <div className="field-info">
                        <span className="char-count">
                          {coverLetterLength}/{MAX_COVER_LETTER_LEN} characters {" "}
                          {coverLetterLength < MIN_COVER_LETTER_LEN && (
                            <span className="text-warning"> (minimum {MIN_COVER_LETTER_LEN} required)</span>
                          )}
                        </span>
                        {fieldErrors.cover_letter && (
                          <span className="error-message">{fieldErrors.cover_letter}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-12">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="resume" className="mb-10">
                          <Translatable>
                            Resume (PDF/DOC)
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <div
                        className={`upload-dropzone${resumeError ? ' upload-error' : ''}`}
                        {...makeDropHandlers(resumeInputRef, setResumeName)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resumeInputRef.current && resumeInputRef.current.click(); } }}
                      >
                        <div>
                          <div><Translatable>{resumeName}</Translatable></div>
                          <div className="upload-actions">
                            <button type="button" className="upload-btn" onClick={() => resumeInputRef.current && resumeInputRef.current.click()}><Translatable>Choose File</Translatable></button>
                          </div>
                        </div>
                      </div>
                      <input
                        ref={resumeInputRef}
                        id="resume"
                        type="file"
                        name="resume"
                        className="hidden"
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        required
                        onChange={(e) => handleFileSelect(e, setResumeName)}
                      />
                    </div>
                  </div>

                  <div className="col-span-12">
                    <div className="form-group">
                      <label htmlFor="notes" className="mb-10">
                        <Translatable>
                          Notes
                        </Translatable>
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        placeholder={rtl ? "هل هناك أي معلومات أخرى يجب أن نعرفها؟" : "Anything else we should know?"}
                        rows={4}
                        maxLength={MAX_NOTES_LEN}
                        onChange={(e) => handleFieldChange('notes', e.target.value)}
                        className={fieldErrors.notes ? 'error' : ''}
                      ></textarea>
                      <div className="field-info">
                        <span className="char-count">
                          {notesLength}/{MAX_NOTES_LEN} characters
                        </span>
                        {fieldErrors.notes && (
                          <span className="error-message">{fieldErrors.notes}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <MsgComponent message={err} className="error-msg"/>


                  <div className="col-span-12">
                    <div className="mt-30">
                      <button disabled={mutation.isPending} type="submit" className="butn butn-full butn-bord radius-30">
                        <span className="text">
                          <Translatable>
                            {mutation.isPending ? "Submitting" : "Submit application"}
                            
                          </Translatable>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
      </div>
    </section>
  );
}

export default JoinForm;

